import { AUTOMAPA_INTAKE_WHATSAPP } from "./leadCaptureConstants";
import { formatVehicleTitle } from "../../utils/automapListing/listingFormatters";
import {
  buildDealerContactMessage,
  buildDealerProfileContactMessage,
  buildWhatsappHref,
  isValidWhatsappNumber,
  normalizeWhatsappNumber,
} from "../../utils/dealerWhatsapp";

export { normalizeWhatsappNumber as normalizePhoneNumber, normalizeWhatsappNumber, isValidWhatsappNumber };

export function resolveDealerWhatsappNumber({ listing, dealer, allowIntakeFallback = true } = {}) {
  const candidate =
    listing?.dealerWhatsapp ||
    listing?.sellerPhone ||
    dealer?.mainWhatsapp ||
    dealer?.whatsapp ||
    dealer?.phone ||
    null;
  if (candidate) {
    const digits = normalizeWhatsappNumber(candidate);
    if (isValidWhatsappNumber(digits)) return digits;
  }
  if (allowIntakeFallback && AUTOMAPA_INTAKE_WHATSAPP) return AUTOMAPA_INTAKE_WHATSAPP;
  return null;
}

export function buildWhatsAppMessage({
  leadType,
  sourceAction,
  listing,
  buyerName,
  buyerIntent,
  recommendation,
  dealer,
  detailUrl,
}) {
  const vehicleTitle =
    formatVehicleTitle(listing || recommendation || {}, { fallback: "vehículo" });

  const dealerName = dealer?.businessName || listing?.sellerName || listing?.dealerName || "su concesionario";
  const greeting = buyerName ? `Hola, soy ${buyerName}.` : "Hola.";

  if (leadType === "dealer_contact" || sourceAction === "whatsapp_contact") {
    return `${greeting} Vi el perfil de ${dealerName} en AutoMapa y me gustaría recibir más información.`;
  }

  if (sourceAction === "similar_options_request") {
    return `${greeting} Estoy revisando el inventario de ${dealerName} en AutoMapa. Me gustaría recibir opciones similares según mi búsqueda.`;
  }

  if (sourceAction === "visit_request") {
    return `${greeting} Vi el perfil de ${dealerName} en AutoMapa. Me gustaría agendar una visita para ver vehículos.`;
  }

  if ((sourceAction === "financing_request" || leadType === "financing_interest") && !listing) {
    return `${greeting} Vi el perfil de ${dealerName} en AutoMapa. Quiero información sobre opciones de financiación.`;
  }

  if (leadType === "quiz_request" || sourceAction === "enviar_solicitud_dealers") {
    const city = buyerIntent?.city || "mi ciudad";
    const budget = buyerIntent?.budgetLabel || "mi presupuesto";
    const type = buyerIntent?.vehicleTypeLabel || "vehículo";
    const timeline = buyerIntent?.timelineLabel || "pronto";
    return `Hola, completé mi búsqueda en AutoMapa. Estoy buscando ${type} en ${city}, ${budget}, con compra probable ${timeline}. Quiero recibir opciones de concesionarios verificados.`;
  }

  if (sourceAction === "confirmar_disponibilidad" || leadType === "availability_request") {
    return `Hola, vi este ${vehicleTitle} en AutoMapa. Me gustaría confirmar disponibilidad, precio final y opciones de financiación.`;
  }

  if (sourceAction === "pedir_mejor_precio" || leadType === "price_request") {
    return `Hola, vi este ${vehicleTitle} en AutoMapa. Me interesa conocer el mejor precio disponible y condiciones de compra.`;
  }

  if (sourceAction === "agendar_test_drive" || leadType === "test_drive_request") {
    return `Hola, vi este ${vehicleTitle} en AutoMapa. Me gustaría agendar un test drive. ¿Qué horarios tienen disponibles?`;
  }

  if (sourceAction === "preguntar_financiacion" || leadType === "financing_interest") {
    return `Hola, vi este ${vehicleTitle} en AutoMapa. Quiero información sobre opciones de financiación y cuota inicial.`;
  }

  if (sourceAction === "solicitar_video" || leadType === "video_request") {
    return `Hola, vi este ${vehicleTitle} en AutoMapa. ¿Podrían enviarme un video del vehículo?`;
  }

  if (leadType === "dealer_profile_contact") {
    return buildDealerProfileContactMessage({
      dealerProfileUrl:
        detailUrl || (typeof window !== "undefined" ? window.location.href : null),
    });
  }

  if (leadType === "vehicle_listing_contact" || leadType === "listing_inquiry" || sourceAction === "contactar_whatsapp") {
    return buildDealerContactMessage({
      listing,
      detailUrl:
        detailUrl || (typeof window !== "undefined" ? window.location.href : null),
    });
  }

  return `${greeting} Vi ${vehicleTitle} en AutoMapa y me gustaría más información para continuar la compra.`;
}

export function buildWhatsAppUrl({ phoneNumber, message }) {
  const href = buildWhatsappHref(phoneNumber, message);
  if (href) return href;
  const fallback = AUTOMAPA_INTAKE_WHATSAPP;
  if (!fallback) return null;
  const digits = normalizeWhatsappNumber(phoneNumber || fallback);
  return `https://wa.me/${digits}?text=${encodeURIComponent(message || "")}`;
}
